import React, { useState } from 'react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import BarChart from '../../components/charts/BarChart';
import HorizontalBarChart from '../../components/charts/HorizontalBarChart';
import LineChart from '../../components/charts/LineChart';
import {
  gujaratDailyGeneration,
  gujaratMonthlyGeneration,
  gujaratHourlyGeneration,
  gujaratPRTrend,
  discomGenerationBars,
  stateTopPlants,
} from '../../data/mockData';

type Tab = 'daily' | 'monthly' | 'hourly';

export default function StateGeneration() {
  const [tab, setTab] = useState<Tab>('daily');

  const chartData =
    tab === 'daily'   ? gujaratDailyGeneration :
    tab === 'monthly' ? gujaratMonthlyGeneration :
    gujaratHourlyGeneration;

  const yUnit = tab === 'hourly' ? ' MW' : ' GWh';

  const tabCls = (t: Tab) =>
    `px-3 py-1.5 text-xs rounded-input border transition-colors ${
      tab === t
        ? 'bg-navy text-white border-navy'
        : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'
    }`;

  return (
    <div className="p-6 space-y-6">
      {/* KPIs — 6 cards, unit-free labels */}
      <div className="flex gap-4">
        <KPICard title="Today's Generation"    value="9.84 GWh"       subtext="+3.2% vs yesterday" subtextColor="green" />
        <KPICard title="This Month"            value="241 GWh"        subtext="Apr 2026" />
        <KPICard title="Lifetime Total"        value="0.84 TWh"       subtext="Since platform launch" />
        <KPICard title="Peak Power Today"      value="1,124 MW"       subtext="At 12:00 PM" />
        <KPICard title="Avg Specific Yield"    value="3.68 kWh/kWp"  subtext="Gujarat today" />
        <KPICard title="Avg Performance Ratio" value="78.2%"          subtext="Gujarat — Good zone" subtextColor="green" />
      </div>

      {/* Main bar chart with tabs */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <SectionDivider title="Date-wise Generation" subtitle="Gujarat — GWh" />
          <div className="flex items-center gap-1.5">
            <button className={tabCls('daily')}   onClick={() => setTab('daily')}>Daily</button>
            <button className={tabCls('monthly')} onClick={() => setTab('monthly')}>Monthly</button>
            <button className={tabCls('hourly')}  onClick={() => setTab('hourly')}>Today Hourly</button>
          </div>
        </div>
        <BarChart data={chartData} unit={yUnit} height={260} />
      </div>

      {/* DISCOM bars + PR trend */}
      <div className="flex gap-4">
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="DISCOM-wise Generation Today" subtitle="GWh" />
          <HorizontalBarChart data={discomGenerationBars} unit=" GWh" defaultColor="#2472C8" />
        </div>

        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="PR Trend — Gujarat" subtitle="Monthly average (Nov–Apr)" />
          <LineChart
            data={gujaratPRTrend}
            xKey="month"
            yKey="value"
            unit="%"
            yDomain={[70, 82]}
            color="#1A6B3A"
            height={200}
          />
        </div>
      </div>

      {/* Top 5 performing plants */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="Top 5 Performing Plants — Gujarat" subtitle="Today's generation" />
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2.5 text-text-tertiary font-medium">Plant ID</th>
              <th className="text-left py-2.5 text-text-tertiary font-medium">District</th>
              <th className="text-right py-2.5 text-text-tertiary font-medium">kWh Today</th>
              <th className="text-right py-2.5 text-text-tertiary font-medium">PR%</th>
            </tr>
          </thead>
          <tbody>
            {stateTopPlants.map(p => (
              <tr key={p.id} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                <td className="py-2.5 font-mono text-text-primary">{p.id}</td>
                <td className="py-2.5 text-text-secondary">{p.district}</td>
                <td className="py-2.5 text-right font-medium text-text-primary">{p.kwhToday}</td>
                <td className="py-2.5 text-right">
                  <span className={`font-semibold ${p.prPct >= 85 ? 'text-success' : 'text-warning'}`}>
                    {p.prPct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
